import React from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import {
  fieldBase, fieldBorder, fieldBorderError, fieldError, fieldHint, fieldLabel, useFieldId,
} from './fieldStyles';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  placeholder?: string;
  /**
   * Whether the empty row is a VALUE the user may come back to, or a PROMPT they
   * must move off.
   *
   * The default (`false`) renders `<option value="" disabled>`, which HTML makes
   * unselectable — right for "Choose a researcher", where empty is the absence
   * of an answer and the submit button is disabled until one exists.
   *
   * It is WRONG, and silently so, wherever empty carries meaning: "Not answered",
   * "Any status", "Whole owner only". There the first click is irreversible —
   * there is no way back to the empty row and no clear control — so a mis-click
   * either strands the user (a filter that can never be widened again) or turns
   * into an assertion nobody made (`is_still_unbuilt: false` means "the
   * researcher looked and it is built"). Pass `placeholderSelectable` there.
   */
  placeholderSelectable?: boolean;
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({
    className, label, error, hint, options, placeholder, placeholderSelectable, id, ...props
  }, ref) => {
    const selectId = useFieldId(id, label);
    const messageId = `${selectId}-message`;
    const hasMessage = Boolean(error || hint);

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={selectId} className={fieldLabel}>
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            aria-invalid={error ? true : undefined}
            aria-describedby={hasMessage ? messageId : undefined}
            className={cn(
              fieldBase,
              'appearance-none cursor-pointer pr-10',
              error ? fieldBorderError : fieldBorder,
              className
            )}
            {...props}
          >
            {placeholder && (
              <option value="" disabled={!placeholderSelectable}>
                {placeholder}
              </option>
            )}
            {options && Array.isArray(options) && options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400 dark:text-gray-500">
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          </div>
        </div>
        {error && (
          <p id={messageId} className={fieldError}>
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={messageId} className={fieldHint}>
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };
